/**
 * What to tell a business about each document it is asked for: what the
 * document is, where to get it, and what makes it quick to approve.
 *
 * Keyed by document_type.code. Staff can add company document types in
 * Mission Control (Settings > Document types > Customer); a code not listed
 * here gets the generic guidance, built from the label they gave it.
 */

export interface DocGuide {
  /** One line: what the document is. */
  what: string
  /** Where a Kenyan business usually gets it. */
  where?: string
  /** What we check, so the customer can check it first. */
  check: string[]
}

const GUIDES: Record<string, DocGuide> = {
  certificate_of_incorporation: {
    what: 'Shows the company is legally registered.',
    where: 'The certificate of incorporation (limited companies) or certificate of registration (business names) from the Business Registration Service, downloadable on eCitizen.',
    check: [
      'The company name matches your Xobo account',
      'The registration number matches the one you gave at signup',
      'The whole certificate is visible, including any seal or QR code',
    ],
  },
  kra_pin_certificate: {
    what: "The company's KRA PIN certificate.",
    where: 'Download it from iTax (itax.kra.go.ke) under Certificates > Reprint PIN Certificate.',
    check: [
      'It is the company’s PIN, not a director’s personal PIN',
      'The PIN matches the one you gave at signup',
      'The PIN and taxpayer name are easy to read',
    ],
  },
  proof_of_address: {
    what: 'Shows where the business operates.',
    where: 'A utility bill, lease or tenancy agreement, or bank statement from the last three months.',
    check: [
      'It is in the company’s name',
      'It shows a physical address',
      'It is dated within the last three months',
    ],
  },
  signatory_id: {
    what: 'Identifies someone allowed to act for the company.',
    where: 'The national ID (both sides) or passport photo page of a director or another authorised signatory.',
    check: [
      'Both sides of an ID card, in one file or as one clear photo',
      'The name and ID number are readable and not covered',
      'It has not expired',
    ],
  },
  cr12: {
    what: 'The official list of current directors and shareholders.',
    where: 'Request a CR12 (official search) on eCitizen. Sole proprietors and partnerships don’t have one.',
    check: ['Issued in the last 12 months', 'Shows the current directors'],
  },
  insurance_certificate: {
    what: 'Shows the goods you send with us are insured.',
    where: 'The certificate or cover note from your insurer.',
    check: ['It is in the company’s name', 'The policy number and cover period are visible', 'It is still valid'],
  },
  service_agreement: {
    what: 'The service agreement you signed with Xobo.',
    where: 'If you signed one with our team, upload the signed copy.',
    check: ['Every page that needs a signature is signed'],
  },
}

/** A label mid-sentence: "Proof of address" → "proof of address", "CR12" stays. */
export const inSentence = (label: string) =>
  /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label

export function guideFor(code: string, label: string): DocGuide {
  return GUIDES[code] || {
    what: `Upload a clear copy of your ${inSentence(label)}.`,
    check: ['It is in the company’s name', 'Every part is readable'],
  }
}

/** Advice that applies to every upload. */
export const GENERAL_TIPS = [
  'A PDF or a sharp photo: all four corners in the frame, no glare, nothing cut off',
  'PDF, JPG or PNG, up to 10 MB',
]
